import { KnowledgeArticleSummary } from '../../chatflows/models/chatflow-runtime.model';

export type ConversationStatus = 'WAITING_AGENT' | 'LIVE_CHAT' | 'CLOSED';

export interface ContactSummary {
  id: number;
  name?: string;
  email?: string;
  phone?: string;
  position?: string;
  companyId?: number;
  companyName?: string;
  lifecycleStage?: string;
  ownerName?: string;
}

export interface CompanySummary {
  id: number;
  name?: string;
  domain?: string;
  country?: string;
  industry?: string;
}

export interface AgentConversationListItem {
  conversationId: number;
  chatflowId: number;
  /** Nombre configurado del chatflow de origen. La bandeja nunca debe enseñar el id (inc47). */
  chatflowName?: string | null;
  /**
   * Título que el agente le puso a la conversación. La bandeja lo prefiere al nombre del
   * visitante: es lo que escribió una persona para reconocerla entre chats anónimos.
   */
  agentTitle?: string | null;
  visitorName: string | null;
  lastMessageAt: string | null;
  status: ConversationStatus;
  assignedAgentId: number | null;
  /** Nombre del agente asignado. Sin él solo se podía pintar "Agente 7". */
  assignedAgentName?: string | null;
  teamId: number | null;
  /** Contacto vinculado: permite filtrar el historial de una ficha. */
  contactId?: number | null;
  /**
   * Nombre de la ficha del CRM vinculada, resuelto por el backend para toda la página de una
   * vez. Es la identidad REAL de quien escribe: {@link visitorName} lo teclea el propio
   * visitante en el widget, y en un chat anónimo no hay ninguno.
   */
  contactName?: string | null;
  /**
   * Mensajes del visitante sin responder (posteriores al último mensaje saliente).
   * El backend lo devolvía fijo a 0, así que la insignia de la bandeja no aparecía nunca.
   * Una conversación cerrada siempre trae 0.
   */
  unreadCount: number;
  escalatedAt: string | null;
}

/**
 * Estados de una conversación, en el idioma de la interfaz.
 *
 * El backend los manda en inglés y en mayúsculas (`WAITING_AGENT`), que es un valor técnico:
 * la bandeja nunca debe pintarlo tal cual.
 */
export const CONVERSATION_STATUS_LABELS: Record<string, string> = {
  WAITING_AGENT: 'En cola',
  IN_QUEUE: 'En cola',
  LIVE_CHAT: 'Activa',
  RESOLVED: 'Resuelta',
  CLOSED: 'Cerrada'
};

/** Etiqueta legible de un estado; si llega uno desconocido, se enseña tal cual antes que vacío. */
export function conversationStatusLabel(status?: string | null): string {
  if (!status) return '';
  return CONVERSATION_STATUS_LABELS[status] ?? status;
}

/**
 * Espeja `AgentConversationDetailResponse` del backend. Los nombres de campo son los del DTO:
 * antes había inventados (`id`, `agentId`, `agentName`, `teamName`, `createdAt`) que llegaban
 * siempre undefined y pintaban "#undefined" en la cabecera de la conversación.
 */
export interface AgentConversationDetail {
  conversationId: number;
  chatflowId?: number | null;
  /** Nombre del chatflow de origen (inc47). */
  chatflowName?: string | null;
  status: ConversationStatus;
  /** Título puesto por el agente (V194). Null = sin título. */
  agentTitle?: string | null;
  visitorName?: string | null;
  visitorEmail?: string | null;
  visitorPhone?: string | null;
  contactId?: number | null;
  companyId?: number | null;
  contactSummary?: ContactSummary | null;
  companySummary?: CompanySummary | null;
  assignedAgentId?: number | null;
  assignedAgentName?: string | null;
  teamId?: number | null;
  lastMessageAt?: string | null;
  escalatedAt?: string | null;
  queueEnteredAt?: string | null;
  closedAt?: string | null;
  closedByUserId?: number | null;
  closeReason?: string | null;
  /** Ticket ya creado desde esta conversación (inc68). Sobrevive a recargar la bandeja. */
  linkedTicketId?: number | null;
  /** Número público del ticket vinculado (TKT-1-100). Es lo que se enseña, nunca el id. */
  linkedTicketNumber?: string | null;
}

export interface DealSummary {
  id: number;
  name?: string;
  stage?: string;
  amount?: number;
  currency?: string;
  ownerName?: string;
  closingDate?: string;
}

export interface PreviousTicketSummary {
  id: number;
  ticketNumber?: string;
  subject?: string;
  status?: string;
  priority?: string;
  createdAt?: string;
  closedAt?: string;
}

/**
 * Espeja `AgentConversationCrmContextResponse.ConversationHistoryItem` del backend.
 * Antes declaraba campos que ese DTO nunca ha mandado (`chatflowId`, `assignedAgentId`,
 * `lastMessageAt`, `summary`), así que el panel pintaba "Sin mensajes / Sin agente" siempre.
 */
export interface PreviousConversationSummary {
  conversationId: number;
  status: ConversationStatus;
  startedAt?: string | null;
  closedAt?: string | null;
  subject?: string | null;
  lastMessagePreview?: string | null;
}

/** Returned by GET /api/agent/conversations/{id}/crm-context */
export interface CrmConversationContext {
  conversationId: number;
  contactId?: number | null;
  companyId?: number | null;
  knownVisitor?: boolean;
  visitorEmail?: string | null;
  visitorPhone?: string | null;
  visitorName?: string | null;
  /** Idioma preferido detectado en el runtime */
  preferredLanguage?: string | null;
  contactSummary?: ContactSummary | null;
  companySummary?: CompanySummary | null;
  /** RF-05: Deals/Negocios activos del contacto */
  deals?: DealSummary[];
  /** RF-05: Tickets anteriores del contacto */
  previousTickets?: PreviousTicketSummary[];
  /** RF-05: Conversaciones de chat anteriores del mismo contacto */
  previousConversations?: PreviousConversationSummary[];
  /** RF-08: Artículos sugeridos durante el bot */
  knowledgeSuggestions?: KnowledgeArticleSummary[];
  /** Datos capturados en el flujo (ej. {email, phone}) */
  capturedData?: Record<string, string>;
}

/** Filtros de GET /api/agent/conversations. Solo los que el controller entiende. */
export interface AgentConversationFilter {
  status?: ConversationStatus | 'ALL';
  assignedToMe?: boolean;
  /** Historial de una ficha: solo las conversaciones de ese contacto. */
  contactId?: number;
}

// RF-08: Knowledge suggestions
export interface KnowledgeSuggestion {
  articleId: number;
  title: string;
  slug: string;
  summary?: string;
  category?: string;
  language?: string;
}

export interface KnowledgeMetrics {
  chatflowId: number;
  totalKnowledgeSuggestions: number;
  totalResolved: number;
  totalNotResolved: number;
  totalNoResults: number;
  deflectionRate: number;
  topSuggestedArticles: { articleId: number; title: string; slug: string; occurrences: number }[];
  topNoResultQueries: { searchTerm: string; occurrences: number }[];
}
